import { getBlockDefinition } from "./blocks/registry";
import { createButtonRowBlock } from "./blocks/button-row";
import {
  presetById,
  ratiosToPercents,
  type ColumnPresetId,
} from "./column-presets";
import { uid, ZERO_PADDING, ZERO_RADIUS, DEFAULT_FONT } from "./create-shared";
import { DEFAULT_IMAGE_OVERLAY } from "./image-overlay";
import type {
  Block,
  BlockType,
  Column,
  EmailDocument,
  EmailSettings,
  Group,
  Padding,
  Row,
} from "./types";
import {
  EMAIL_BUILDER_COLOR_BACKGROUND,
  EMAIL_BUILDER_COLOR_BLUE_600,
  EMAIL_BUILDER_COLOR_BUTTON_BORDER,
  EMAIL_BUILDER_COLOR_BUTTON_TEXT,
  EMAIL_BUILDER_COLOR_DIVIDER,
  EMAIL_BUILDER_COLOR_GRAY_500,
  EMAIL_BUILDER_COLOR_HEADING,
  EMAIL_BUILDER_COLOR_PAGE_BACKGROUND,
} from '../schema/emailBuilderDesignColors';
import { defaultSurfaceBorder } from "../operations/emailBuilderBandBorder";
import { EMAIL_DOCUMENT_SCHEMA_VERSION } from "./types";

export const CONTENT_GUTTER = 24;

export const DEFAULT_ROW_PADDING: Padding = {
  top: 24,
  right: 24,
  bottom: 24,
  left: 24,
};

export const DEFAULT_GROUP_PADDING: Padding = {
  ...DEFAULT_ROW_PADDING,
};

export const GROUPED_ROW_PADDING: Padding = {
  top: 24,
  right: 0,
  bottom: 24,
  left: 0,
};

export function columnContentGutter(
  columnIndex: number,
  columnCount: number,
): Pick<Padding, 'left' | 'right'> {
  if (columnCount <= 1) {
    return { left: CONTENT_GUTTER, right: CONTENT_GUTTER };
  }
  if (columnIndex === 0) {
    return { left: CONTENT_GUTTER, right: 0 };
  }
  if (columnIndex === columnCount - 1) {
    return { left: 0, right: CONTENT_GUTTER };
  }
  return { left: 0, right: 0 };
}

export function rowHasContentGutter(row: Row): boolean {
  return row.contentGutter !== false;
}

export function applyRowColumnGutters(row: Row): void {
  const count = row.columns.length;
  for (let index = 0; index < count; index += 1) {
    const column = row.columns[index];
    if (!column) {
      continue;
    }
    const gutter = columnContentGutter(index, count);
    column.padding = {
      ...column.padding,
      left: gutter.left,
      right: gutter.right,
    };
  }
}

export { DEFAULT_FONT } from "./create-shared";

export const DEFAULT_HEADING_COLOR = EMAIL_BUILDER_COLOR_HEADING;

export const DEFAULT_PARAGRAPH_COLOR = EMAIL_BUILDER_COLOR_GRAY_500;

export const DEFAULT_LINK_COLOR = EMAIL_BUILDER_COLOR_BLUE_600;

export const DEFAULT_DIVIDER_COLOR = EMAIL_BUILDER_COLOR_DIVIDER;

export const DEFAULT_BUTTON_FILL_COLOR = EMAIL_BUILDER_COLOR_BLUE_600;

export const DEFAULT_BUTTON_TEXT_COLOR = EMAIL_BUILDER_COLOR_BUTTON_TEXT;

export const DEFAULT_BUTTON_BORDER_COLOR = EMAIL_BUILDER_COLOR_BUTTON_BORDER;

export function defaultSettings(): EmailSettings {
  return {
    contentWidth: 600,
    pageBackgroundColor: EMAIL_BUILDER_COLOR_PAGE_BACKGROUND,
    contentBackgroundColor: EMAIL_BUILDER_COLOR_BACKGROUND,
    pagePadding: { ...ZERO_PADDING },
    headingFontFamily: DEFAULT_FONT,
    bodyFontFamily: DEFAULT_FONT,
    headingColor: DEFAULT_HEADING_COLOR,
    paragraphColor: DEFAULT_PARAGRAPH_COLOR,
    linkColor: DEFAULT_LINK_COLOR,
    dividerColor: DEFAULT_DIVIDER_COLOR,
    buttonFillColor: DEFAULT_BUTTON_FILL_COLOR,
    buttonTextColor: DEFAULT_BUTTON_TEXT_COLOR,
    buttonBorderColor: DEFAULT_BUTTON_BORDER_COLOR,
    lang: 'en',
    dir: 'ltr',
  };
}

export function createBlock(type: BlockType, settings?: EmailSettings): Block {
  if (type === "buttonRow") {
    return createButtonRowBlock(settings);
  }
  return getBlockDefinition(type).create();
}

export function createColumn(blocks: Block[] = []): Column {
  return {
    id: uid(),
    backgroundColor: 'transparent',
    backgroundImage: '',
    backgroundImageOverlay: { ...DEFAULT_IMAGE_OVERLAY },
    padding: { ...ZERO_PADDING },
    border: defaultSurfaceBorder(),
    borderRadius: { ...ZERO_RADIUS },
    verticalAlign: 'top',
    blocks,
  };
}

export type CreateRowOptions = {
  contentGutter?: boolean;
  nestedInGroup?: boolean;
};

export function createRow(
  presetId: ColumnPresetId = 'one',
  blocksPerColumn?: Block[][],
  options: CreateRowOptions = {},
): Row {
  const { contentGutter = false, nestedInGroup = false } = options;
  const preset = presetById(presetId);
  const columns = preset.ratios.map((_, index) =>
    createColumn(blocksPerColumn?.[index] ?? []),
  );
  const row: Row = {
    id: uid(),
    backgroundColor: 'transparent',
    backgroundImage: '',
    backgroundImageOverlay: { ...DEFAULT_IMAGE_OVERLAY },
    padding: nestedInGroup
      ? { ...GROUPED_ROW_PADDING }
      : { ...DEFAULT_ROW_PADDING },
    border: defaultSurfaceBorder(),
    borderRadius: { ...ZERO_RADIUS },
    columns,
    columnWidths: ratiosToPercents(preset.ratios),
    columnGap: columns.length > 1 ? 16 : 0,
    responsive: true,
    contentGutter,
  };
  if (contentGutter) {
    applyRowColumnGutters(row);
  }
  return row;
}

export type CreateGroupOptions = {
  withDefaultPadding?: boolean;
};

export function createGroup(
  rows: Row[] = [],
  options: CreateGroupOptions = {},
): Group {
  const withDefaultPadding = options.withDefaultPadding ?? rows.length === 0;

  return {
    id: uid(),
    backgroundColor: 'transparent',
    backgroundImage: '',
    backgroundImageOverlay: { ...DEFAULT_IMAGE_OVERLAY },
    padding: withDefaultPadding
      ? { ...DEFAULT_GROUP_PADDING }
      : { ...ZERO_PADDING },
    border: defaultSurfaceBorder(),
    borderRadius: { ...ZERO_RADIUS },
    rows,
  };
}

export function createEmptyDocument(): EmailDocument {
  return {
    schemaVersion: EMAIL_DOCUMENT_SCHEMA_VERSION,
    settings: defaultSettings(),
    sections: [createRow('one')],
  };
}

function cloneValue<T>(value: T): T {
  return structuredClone(value);
}

export function cloneBlock(block: Block): Block {
  const next = cloneValue(block);
  next.id = uid();
  return next;
}

export function cloneColumn(column: Column): Column {
  const next = cloneValue(column);
  next.id = uid();
  next.blocks = next.blocks.map(cloneBlock);
  return next;
}

export function cloneRow(row: Row): Row {
  const next = cloneValue(row);
  next.id = uid();
  next.columns = next.columns.map(cloneColumn);
  return next;
}

export function cloneGroup(group: Group): Group {
  const next = cloneValue(group);
  next.id = uid();
  next.rows = next.rows.map(cloneRow);
  return next;
}
